// src/components/ReferralUpsell.tsx
//
// Post-payment "Refer & Earn" upsell (app parity — referral_upsell_sheet.dart).
// Any successful payment dispatches a `aura:payment-success` window event; this
// listens for it, pulls the user's referral code (get_referral_context) and, if
// they have one, slides up a share prompt. Mounted once, globally, in App.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IconGift, IconX, IconCopy, IconCheck, IconShare3 } from "@tabler/icons-react";
import { supabase } from "../supabaseClient";
import { usePricing } from "../context/PricingContext";
import styles from "./ReferralUpsell.module.css";

export const PAYMENT_SUCCESS_EVENT = "aura:payment-success";

interface RefCtx {
  code: string | null;
  share_url: string | null;
  referrer_reward_minor: number;
  referee_reward_minor: number;
}

export default function ReferralUpsell() {
  const navigate = useNavigate();
  const { formatPrice } = usePricing();
  const [ctx, setCtx] = useState<RefCtx | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const onPaid = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("get_referral_context");
      if (error || !data) return;
      const c = data as RefCtx;
      if (!c.code) return; // nothing to share → skip silently
      // Delay a beat so it lands after the success toast, like the app.
      setCtx(c);
      setTimeout(() => setOpen(true), 900);
    } catch {
      /* non-fatal — the upsell is a nicety */
    }
  }, []);

  useEffect(() => {
    window.addEventListener(PAYMENT_SUCCESS_EVENT, onPaid);
    return () => window.removeEventListener(PAYMENT_SUCCESS_EVENT, onPaid);
  }, [onPaid]);

  if (!open || !ctx?.code) return null;

  const reward = formatPrice(ctx.referrer_reward_minor);
  const shareMessage = `Check out Aura AI — your personal Vedic astrologer. Use my code ${ctx.code} when you sign up & recharge and we both get ${reward}! ${ctx.share_url || "https://astroaura.ai"}`;

  const copy = () => {
    navigator.clipboard?.writeText(ctx.code || "");
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 1500);
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ text: shareMessage }); } catch { /* cancelled */ }
    } else {
      navigator.clipboard?.writeText(shareMessage);
      toast.success("Invite copied");
    }
  };

  return (
    <div className={styles.overlay} onClick={() => setOpen(false)}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <button className={styles.close} onClick={() => setOpen(false)} aria-label="Close">
          <IconX size={20} />
        </button>
        <span className={styles.badge}><IconGift size={26} /></span>
        <h3 className={styles.title}>Loved it? Share the stars ✨</h3>
        <p className={styles.sub}>
          Give friends {formatPrice(ctx.referee_reward_minor)} off and get{" "}
          <strong>{reward}</strong> in your wallet for every friend who recharges.
        </p>

        <div className={styles.codeRow}>
          <span className={styles.code}>{ctx.code}</span>
          <button className={styles.copyBtn} onClick={copy}>
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <button className={styles.shareBtn} onClick={share}>
          <IconShare3 size={17} /> Share invite
        </button>
        <button
          className={styles.moreBtn}
          onClick={() => { setOpen(false); navigate("/referral"); }}
        >
          Refer &amp; Earn dashboard
        </button>
      </div>
    </div>
  );
}
